import React, { useEffect } from 'react';
import {
    CheckIcon,
    CornersInIcon,
    CropIcon,
    FrameCornersIcon,
    BroadcastIcon,
    ArrowClockwiseIcon,
    ArrowCounterClockwiseIcon,
    MagnifyingGlassPlusIcon,
    TimerIcon,
    XIcon,
} from '@phosphor-icons/react';
import { Pause, Play, Repeat1 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { createPlayerKeyHandler, PLAYBACK_SPEEDS } from '@/utils/video-player';
import { SKIP_SECONDS } from '@/constants/video-player';
import { cn } from '@/lib/utils';
import { SkipDirection } from './hooks/useSkipIndicator';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItemBase,
    DropdownMenuLabel,
    DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/Tooltip';
import { KeyHint } from '@/components';
import { useVideoPlayerContext } from './VideoPlayerProvider';
import { useVideoLoopPlayback } from './hooks/useVideoLoopPlayback';
import { usePlaybackRate } from './hooks/usePlaybackRate';

interface VideoPlayerControlsProps {
    expanded: boolean;
}

export const VideoPlayerControls: React.FC<VideoPlayerControlsProps> = ({
    expanded,
}) => {
    const { t } = useTranslation(I18nNamespace.VIDEO);
    const ctx = useVideoPlayerContext();

    const {
        streaming,
        isCamera,
        toggleExpanded,
        onSkip,
        playing,
        togglePlay,
        loop,
        setLoop,
        cropEnabled,
        croppingMode,
        setCroppingMode,
        zoomed,
        toggleZoom,
        toggleZoomLock,
    } = ctx;

    useVideoLoopPlayback();
    const { playbackRate, setRate: handleSetPlaybackRate } = usePlaybackRate();

    const liveIndicatorClassName = cn(
        'flex items-center gap-1.5 text-sm font-normal transition-colors duration-200',
        streaming.canGoLive
            ? 'cursor-pointer hover:opacity-80'
            : 'cursor-not-allowed opacity-50',
        streaming.live && playing ? 'text-success-100' : 'text-grey-300',
    );

    const loopTitle = t(loop ? 'controls.loopOn' : 'controls.loopOff');

    const skip = (seconds: number, direction?: SkipDirection) => {
        const skipDirection =
            direction ??
            (seconds < 0 ? SkipDirection.Backward : SkipDirection.Forward);

        onSkip(seconds, skipDirection);
    };

    const toggleLoop = () => {
        setLoop(!loop);
    };

    const toggleCroppingMode = () => {
        setCroppingMode(!croppingMode);
    };

    useEffect(() => {
        const handleKeyDown = createPlayerKeyHandler({
            live: streaming.live,
            togglePlay,
            skip,
            skipSeconds: SKIP_SECONDS,
            zoomed,
            onToggleZoom: toggleZoom,
            onExitZoom: toggleZoom,
            onToggleZoomLock: toggleZoomLock,
            onToggleCrop: cropEnabled ? toggleCroppingMode : undefined,
            onToggleLoop: toggleLoop,
        });

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [
        streaming.live,
        togglePlay,
        skip,
        zoomed,
        toggleZoom,
        toggleZoomLock,
        cropEnabled,
        toggleCroppingMode,
        toggleLoop,
    ]);

    return (
        <div className={cn('w-full px-4', expanded && 'px-0')}>
            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 h-12">
                <div className="flex items-center gap-4 min-w-0">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="inline-flex">
                                <button
                                    onClick={toggleLoop}
                                    disabled={streaming.live}
                                    aria-label={loopTitle}
                                    className="rounded-lg transition-colors disabled:cursor-not-allowed"
                                >
                                    <Repeat1
                                        className={cn(
                                            'size-5 transition-all',
                                            loop
                                                ? 'text-danger-200'
                                                : 'text-grey-200',
                                            streaming.live && 'text-grey-400',
                                        )}
                                    />
                                </button>
                            </span>
                        </TooltipTrigger>
                        <TooltipContent>
                            {loopTitle}
                            {!zoomed && <KeyHint theme="light">L</KeyHint>}
                        </TooltipContent>
                    </Tooltip>

                    {cropEnabled && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    onClick={toggleCroppingMode}
                                    aria-label={
                                        croppingMode
                                            ? t('controls.exitCropMode')
                                            : t('controls.cropImage')
                                    }
                                    className={cn(
                                        'flex items-center rounded-full py-1 transition-all cursor-pointer',
                                        croppingMode
                                            ? 'gap-2 bg-grey-600 px-1.5 text-white-100 hover:bg-surface-hover-strong'
                                            : 'text-grey-200 hover:text-white-100',
                                    )}
                                >
                                    <CropIcon className="size-5" />
                                    {croppingMode && (
                                        <>
                                            <span className="text-sm font-normal leading-5">
                                                {t('controls.cropping')}
                                            </span>
                                            <XIcon className="size-3" />
                                        </>
                                    )}
                                </button>
                            </TooltipTrigger>
                            <TooltipContent>
                                {croppingMode
                                    ? t('controls.exitCropMode')
                                    : t('controls.cropImage')}
                                <KeyHint theme="light">C</KeyHint>
                            </TooltipContent>
                        </Tooltip>
                    )}

                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                onClick={(e) => {
                                    toggleZoom();
                                    // Release focus so playback shortcuts (arrows,
                                    // space) aren't swallowed by the focused button.
                                    e.currentTarget.blur();
                                }}
                                aria-pressed={zoomed}
                                aria-label={
                                    zoomed
                                        ? t('controls.exitZoom')
                                        : t('controls.zoomIn')
                                }
                                className={cn(
                                    'flex items-center rounded-full py-1 transition-all cursor-pointer',
                                    zoomed
                                        ? 'gap-2 bg-grey-600 px-1.5 text-white-100 hover:bg-surface-hover-strong'
                                        : 'text-grey-200 hover:text-white-100',
                                )}
                            >
                                <MagnifyingGlassPlusIcon className="size-5" />
                                {zoomed && (
                                    <>
                                        <span className="text-sm font-normal leading-5">
                                            {t('controls.zoomed')}
                                        </span>
                                        <XIcon className="size-3" />
                                    </>
                                )}
                            </button>
                        </TooltipTrigger>
                        <TooltipContent>
                            {zoomed
                                ? t('controls.exitZoom')
                                : t('controls.zoomIn')}
                            {zoomed ? (
                                <KeyHint theme="light" size="esc">
                                    Esc
                                </KeyHint>
                            ) : (
                                <KeyHint theme="light">Z</KeyHint>
                            )}
                        </TooltipContent>
                    </Tooltip>

                    {isCamera && (streaming.canGoLive || streaming.live) && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="inline-flex">
                                    <button
                                        onClick={
                                            streaming.live
                                                ? streaming.onExitLive
                                                : streaming.onGoLive
                                        }
                                        disabled={!streaming.canGoLive}
                                        className={liveIndicatorClassName}
                                    >
                                        <BroadcastIcon
                                            size={14}
                                            weight="fill"
                                        />
                                        <span>{t('controls.live')}</span>
                                    </button>
                                </span>
                            </TooltipTrigger>
                            <TooltipContent>
                                {streaming.live
                                    ? t('controls.exitLiveStream')
                                    : streaming.canGoLive
                                      ? t('controls.goToLiveStream')
                                      : t('controls.cameraOffline')}
                            </TooltipContent>
                        </Tooltip>
                    )}
                </div>

                <div className="flex items-center gap-3">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="inline-flex">
                                <button
                                    onClick={() =>
                                        skip(
                                            -SKIP_SECONDS,
                                            SkipDirection.Backward,
                                        )
                                    }
                                    aria-label={t('controls.skipBackward', {
                                        seconds: SKIP_SECONDS,
                                    })}
                                    className="transition-colors p-1.5 relative text-grey-200 enabled:hover:text-white-100 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                                    disabled={streaming.live}
                                >
                                    <ArrowCounterClockwiseIcon
                                        className="size-6"
                                        fill="currentColor"
                                    />
                                    <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold font-geist-mono leading-none pt-[3px]">
                                        {SKIP_SECONDS}
                                    </span>
                                </button>
                            </span>
                        </TooltipTrigger>
                        <TooltipContent>
                            {t('controls.skipBackward', {
                                seconds: SKIP_SECONDS,
                            })}
                            <KeyHint theme="light">←</KeyHint>
                        </TooltipContent>
                    </Tooltip>

                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                onClick={togglePlay}
                                aria-label={
                                    playing
                                        ? t('controls.pause')
                                        : t('controls.play')
                                }
                                className="transition-colors rounded-full w-12 h-12 flex items-center justify-center text-grey-200 hover:text-white-100 bg-grey-600 cursor-pointer"
                            >
                                {playing ? (
                                    <Pause
                                        className="size-6"
                                        fill="currentColor"
                                    />
                                ) : (
                                    <Play
                                        className="size-6"
                                        fill="currentColor"
                                    />
                                )}
                            </button>
                        </TooltipTrigger>
                        <TooltipContent>
                            {playing ? t('controls.pause') : t('controls.play')}
                            <KeyHint
                                theme="light"
                                size="esc"
                                className="w-auto"
                            >
                                Space
                            </KeyHint>
                        </TooltipContent>
                    </Tooltip>

                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="inline-flex">
                                <button
                                    onClick={() =>
                                        skip(
                                            SKIP_SECONDS,
                                            SkipDirection.Forward,
                                        )
                                    }
                                    aria-label={t('controls.skipForward', {
                                        seconds: SKIP_SECONDS,
                                    })}
                                    className="transition-colors p-1.5 relative text-grey-200 enabled:hover:text-white-100 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                                    disabled={streaming.live}
                                >
                                    <ArrowClockwiseIcon
                                        className="size-6"
                                        fill="currentColor"
                                    />
                                    <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold font-geist-mono leading-none pt-[3px]">
                                        {SKIP_SECONDS}
                                    </span>
                                </button>
                            </span>
                        </TooltipTrigger>
                        <TooltipContent>
                            {t('controls.skipForward', {
                                seconds: SKIP_SECONDS,
                            })}
                            <KeyHint theme="light">→</KeyHint>
                        </TooltipContent>
                    </Tooltip>
                </div>

                <div className="flex items-center justify-end gap-4">
                    {!streaming.live && (
                        <DropdownMenu modal={false}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <DropdownMenuTrigger className="flex items-center gap-1 text-xs font-medium text-grey-200 hover:text-white-100 transition-colors cursor-pointer">
                                        <TimerIcon className="size-5" />
                                        {`${playbackRate}x`}
                                    </DropdownMenuTrigger>
                                </TooltipTrigger>
                                <TooltipContent>
                                    {t('controls.playbackSpeed', {
                                        rate: playbackRate,
                                    })}
                                </TooltipContent>
                            </Tooltip>
                            <DropdownMenuContent
                                variant="compact"
                                align="end"
                                side="top"
                            >
                                <DropdownMenuLabel>
                                    {t('controls.playbackSpeedLabel')}
                                </DropdownMenuLabel>
                                {PLAYBACK_SPEEDS.map((speed) => (
                                    <DropdownMenuItemBase
                                        key={speed}
                                        onClick={() =>
                                            handleSetPlaybackRate(speed)
                                        }
                                        className={cn(
                                            'justify-between text-xs text-white-100 py-1 hover:!bg-transparent focus:!bg-transparent',
                                            playbackRate === speed &&
                                                'bg-grey-500 hover:!bg-grey-500 focus:!bg-grey-500',
                                        )}
                                    >
                                        {speed === 1
                                            ? `1x - ${t('controls.normal')}`
                                            : `${speed}x`}
                                        {playbackRate === speed && (
                                            <CheckIcon className="size-4 text-grey-200 flex-shrink-0" />
                                        )}
                                    </DropdownMenuItemBase>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                onClick={toggleExpanded}
                                aria-label={
                                    expanded
                                        ? t('controls.exitExpandedMode')
                                        : t('controls.expand')
                                }
                                className="transition-colors text-grey-200 hover:text-white-100 cursor-pointer"
                            >
                                {expanded ? (
                                    <CornersInIcon className="w-5 h-5" />
                                ) : (
                                    <FrameCornersIcon className="size-5" />
                                )}
                            </button>
                        </TooltipTrigger>
                        <TooltipContent>
                            {expanded
                                ? t('controls.exitExpandedMode')
                                : t('controls.expand')}
                        </TooltipContent>
                    </Tooltip>
                </div>
            </div>
        </div>
    );
};
